import React from "react";
import ReactDOM from "react-dom";

import { type BaseEmoji, type CustomEmoji, NimbleEmojiIndex } from "emoji-mart";
import BASE_EMOJI_DATA from "emoji-mart/data/all.json";
import { EditorState, Plugin, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { CustomEmojiManager } from "~/common/Emoji";

import { Extension } from "../extension";
import CustomEmojiNode from "../nodes/custom-emoji";
import { TypeAheadProvider, type TypeAheadResult, type TypeAheadState } from "./typeahead";

import emojiItemStyles from "~/lib/prose/css/emojiItem.module.css";

declare module "emoji-mart" {
	interface NimbleEmojiIndex {
		search(query: string, { custom }: { custom: CustomEmoji[] }): EmojiSearchResult[];
	}
}

// To match the output custom emojis of NimbleEmojiIndex.search
export interface CustomEmojiSearchResult extends CustomEmoji {
	custom: true;
	customCategory?: string;
}
export type EmojiSearchResult = BaseEmoji | CustomEmojiSearchResult;

export const customEmojiIndex = new NimbleEmojiIndex(BASE_EMOJI_DATA);
export default class EmojiProvider extends TypeAheadProvider {
	get key() {
		return ":";
	}

	get replaceMatch() {
		return false;
	}

	search(view: EditorView, searchString: string): TypeAheadResult[] {
		if (searchString.length < 1) {
			return [];
		}

		let customEmojiList: CustomEmoji[] = [];
		const customEmojiExt = view.extensions.get("custom_emoji");
		if (customEmojiExt && customEmojiExt instanceof CustomEmojiNode) {
			customEmojiList = CustomEmojiManager.getInstance().getEmojisList(customEmojiExt.accountId);
		}

		const searchResult = customEmojiIndex.search(searchString, {
			custom: customEmojiList,
		});
		if (!searchResult) {
			return [];
		}

		return searchResult.map(emoji => ({
			id: emoji.id || "",
			title: emoji.colons || "",
			command: () => {
				return (state: EditorState, dispatch: (tr: Transaction) => void) => {
					const tr = state.tr;
					if ("custom" in emoji && emoji.custom === true) {
						const replaceFrom = state.selection.$anchor.pos - searchString.length - 1;
						const replaceTo = state.selection.$anchor.pos;
						CustomEmojiNode.insertEmoji(emoji, replaceFrom, replaceTo)(state, dispatch);
					} else {
						const icon = "native" in emoji ? emoji.native : emoji.name;
						tr.deleteRange(state.selection.$anchor.pos - searchString.length - 1, state.selection.$anchor.pos);
						tr.insertText(icon);
						dispatch(tr);
					}
					return true;
				};
			},
			render: (container: HTMLElement) => {
				if ("custom" in emoji && emoji.custom === true) {
					ReactDOM.render(<CustomEmojiItem emoji={emoji.imageUrl} title={emoji.colons || ""} />, container);
				} else {
					const icon = "native" in emoji ? emoji.native : emoji.name;
					ReactDOM.render(<EmojiItem emoji={icon} title={emoji.colons || ""} />, container);
				}
				return true;
			},
			destroy: (view: EditorView, state: TypeAheadState, container: HTMLElement | undefined) => {
				if (container) {
					ReactDOM.unmountComponentAtNode(container);
				}
			},
		}));
	}
}

const CustomEmojiItem = ({ emoji, title }: { emoji: string; title: string }) => {
	return (
		<div className={emojiItemStyles.emojiItem}>
			<div className={emojiItemStyles.emoji}>
				<img className={emojiItemStyles.customEmoji} src={emoji} alt={title} />
			</div>
			<div className={emojiItemStyles.text}>{title}</div>
		</div>
	);
};

const EmojiItem = ({ emoji, title }: { emoji: string; title: string }) => {
	return (
		<div className={emojiItemStyles.emojiItem}>
			<div className={emojiItemStyles.emoji}>{emoji}</div>
			<div className={emojiItemStyles.text}>{title}</div>
		</div>
	);
};

// Since we allow copying custom emojis from Slack, the built-in ones also need some support
export class PasteBuiltInEmoji extends Extension {
	name = "built-in-emoji-paste-plugin";
	get plugins() {
		return [
			new Plugin({
				props: {
					transformPastedHTML(html) {
						// look for built in emoji from emoji-mart base on a dataset attribute set by Slack
						function matchEmojiWithEl(element: Element) {
							const emojiId = element.getAttribute("data-stringify-emoji")?.split(":").join("");
							if (!emojiId) return null;
							const search = customEmojiIndex.search(emojiId);
							if (!search) return null;
							const exactMatch = search.filter(e => e.id === emojiId);
							if (!exactMatch.length) return null;
							const emoji = exactMatch[0];
							const icon = "native" in emoji ? emoji.native : emoji.name;
							return icon;
						}

						const doc = new DOMParser().parseFromString(html, "text/html");
						// There are two cases when copying an emoji from Slack:
						// when the emoji is in a paragraph, or when it is stand alone
						const inlineSlackEmoji = doc.querySelector("span.c-emoji");
						if (inlineSlackEmoji) {
							const imgEl = inlineSlackEmoji.querySelector('img[data-stringify-type="emoji"]');
							if (imgEl) {
								const matchedEmoji = matchEmojiWithEl(imgEl);
								if (matchedEmoji) {
									inlineSlackEmoji.innerHTML = `<span>${matchedEmoji}</span>`;
									return doc.documentElement.innerHTML;
								}
							}
						}
						const standAloneSlackEmoji = doc.querySelector('img.c-emoji[data-stringify-type="emoji"]');
						if (standAloneSlackEmoji) {
							const matchedEmoji = matchEmojiWithEl(standAloneSlackEmoji);
							if (matchedEmoji) return `<span>${matchedEmoji}<span>`;
						}
						return html;
					},
				},
			}),
		];
	}
}
